import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import {
  Storefront,
  Receipt,
  ListBullets,
  Package,
  ChartLineUp,
  Gear,
  SignOut,
  Buildings,
  Users,
  CaretDown,
  Wallet,
  CurrencyInr,
  ChartPieSlice,
} from "@phosphor-icons/react";

export default function Layout() {
  const { staff, logout } = useAuth();
  const { outlets, activeOutletId, setActiveOutletId } = useOutlet();
  const nav = useNavigate();

  const linkClass = ({ isActive }) => `nav-link ${isActive ? "active" : ""}`;

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const isSuper = staff?.role === "super_admin";
  const isAdmin = isSuper || staff?.role === "admin";

  const activeOutletLabel = (() => {
    if (!isSuper) {
      const me = outlets.find((o) => o.id === staff?.outlet_id);
      return me?.name || "—";
    }
    if (!activeOutletId) return "All outlets";
    return outlets.find((o) => o.id === activeOutletId)?.name || "—";
  })();

  return (
    <div className="min-h-screen bg-boba-cream boba-grain flex">
      <aside className="w-56 bg-white border-r border-boba-stoneLine flex flex-col" data-testid="main-sidebar">
        <div className="p-5 border-b border-boba-stoneLine flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_boba-pos/artifacts/z8fez4xl_WhatsApp%20Image%202026-06-22%20at%209.29.05%20PM.jpeg"
            alt="Hizli"
            className="w-12 h-12 object-cover rounded-lg shadow-sm"
            data-testid="sidebar-logo"
          />
          <div>
            <div className="font-display text-xl font-black text-[#D62A35] leading-none tracking-tight">HIZLI</div>
            <div className="text-[10px] text-boba-inkSoft uppercase tracking-widest font-mono mt-1">POS · v1.0</div>
          </div>
        </div>

        {/* Outlet switcher */}
        <div className="px-3 pt-3" data-testid="outlet-switcher">
          <div className="relative">
            <select
              value={activeOutletId || ""}
              onChange={(e) => setActiveOutletId(e.target.value)}
              disabled={!isSuper}
              className="w-full text-left h-10 pl-3 pr-8 bg-boba-cream border border-boba-stoneLine rounded-md text-sm font-medium appearance-none cursor-pointer disabled:cursor-default disabled:opacity-80"
              data-testid="outlet-select"
              title={activeOutletLabel}
            >
              {isSuper && <option value="">All outlets</option>}
              {outlets.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <CaretDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-boba-inkSoft pointer-events-none" />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-boba-inkSoft font-mono mt-1.5 px-1">
            Active outlet
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 mt-2">
          <NavLink to="/pos" className={linkClass} data-testid="nav-pos">
            <Storefront size={20} weight="duotone" /> Point of Sale
          </NavLink>
          <NavLink to="/orders" className={linkClass} data-testid="nav-orders">
            <Receipt size={20} weight="duotone" /> Orders
          </NavLink>
          <div className="px-3 pt-4 pb-1 text-[10px] tracking-widest uppercase text-boba-inkSoft font-mono">Outlet</div>
          <NavLink to="/dashboard" className={linkClass} data-testid="nav-dashboard">
            <ChartLineUp size={20} weight="duotone" /> Dashboard
          </NavLink>
          <NavLink to="/expenses" className={linkClass} data-testid="nav-expenses">
            <Wallet size={20} weight="duotone" /> Expenses
          </NavLink>
          {isAdmin && (
            <>
              <NavLink to="/inventory" className={linkClass} data-testid="nav-inventory">
                <Package size={20} weight="duotone" /> Inventory
              </NavLink>
              <NavLink to="/settings" className={linkClass} data-testid="nav-settings">
                <Gear size={20} weight="duotone" /> Settings
              </NavLink>
            </>
          )}
          {isSuper && (
            <>
              <div className="px-3 pt-4 pb-1 text-[10px] tracking-widest uppercase text-boba-inkSoft font-mono">Master</div>
              <NavLink to="/menu" className={linkClass} data-testid="nav-menu">
                <ListBullets size={20} weight="duotone" /> Menu
              </NavLink>
              <NavLink to="/salaries" className={linkClass} data-testid="nav-salaries">
                <CurrencyInr size={20} weight="duotone" /> Salaries
              </NavLink>
              <NavLink to="/pnl" className={linkClass} data-testid="nav-pnl">
                <ChartPieSlice size={20} weight="duotone" /> P&amp;L Report
              </NavLink>
              <NavLink to="/franchisees" className={linkClass} data-testid="nav-franchisees">
                <Users size={20} weight="duotone" /> Franchisees
              </NavLink>
              <NavLink to="/outlets" className={linkClass} data-testid="nav-outlets">
                <Buildings size={20} weight="duotone" /> Outlets
              </NavLink>
            </>
          )}
        </nav>

        <div className="p-3 border-t border-boba-stoneLine">
          <div className="px-3 py-2">
            <div className="text-sm font-semibold text-boba-ink" data-testid="staff-name">{staff?.name}</div>
            <div className="text-xs text-boba-inkSoft capitalize font-mono">{staff?.role?.replace("_", " ")}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-boba-inkSoft hover:bg-stone-100 transition-colors text-sm"
          >
            <SignOut size={18} /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
